/**
 * A text note as a FILE — where a new one goes, and which part of an existing
 * one a plain editor is allowed to touch.
 *
 * Writing a note from the web is not a new entity: a manifest-less `.md`
 * outside `arcs/` is imported as a real thought (`VaultImporter` →
 * `ImportManager.importText`) and adopted at the path it was found, gaining
 * its manifest on the next export. So a thought is plain markdown and nothing
 * else. A note a nook holds is the one exception: its front matter carries
 * `origin:`, which makes the importer hold it rather than list it as a
 * thought (`ImportManager.importHeldNote`), and the id it will have.
 *
 * The other half is the harder one. Once that file has been through the app
 * it carries a base64 manifest and content markers, and a plain editor that
 * round-trips the whole file would strand it. So the editable region is
 * derived rather than assumed:
 *
 * - **no markers** — a file nothing has imported yet; all of it is prose.
 * - **one marker pair** — one message, and its inside is the prose. Everything
 *   outside it, manifest included, survives byte for byte.
 * - **more than one** — a recording, a transcript, a topic tree. There is no
 *   honest mapping from a run of edited text back onto separate messages, so
 *   [editableText] returns null and the surface offers the detail view
 *   instead of guessing.
 */

/**
 * One message in its markers. Group 2 is the boundary tag: empty for almost
 * every block, `~N` for one whose content quotes the plain closer. It rides
 * both markers and the closer matches it by backreference, so a quoted closer
 * cannot end the block early — the same grammar `FileVaultSerializer` writes.
 * The empty alternative always participates, so an untagged block reads
 * exactly as it did before tags existed.
 */
export const CONTENT_BLOCK =
  /<!--pondering:content id=([\w-]+)(|~\d+)-->\n([\s\S]*?)\n<!--pondering:\/content\2-->/g;

const CONTENT_CLOSE = '<!--pondering:/content';

/**
 * A boundary tag [content] does not itself contain — the app's own rule
 * (`FileVaultSerializer.closeTagFor`), so a note written here closes exactly
 * as the app would close it. Empty unless the content quotes the closer.
 */
export function closeTagFor(content: string): string {
  if (!content.includes(`${CONTENT_CLOSE}-->`)) return '';
  let n = 1;
  while (content.includes(`${CONTENT_CLOSE}~${n}-->`)) n++;
  return `~${n}`;
}

/** The markers around [content] for message [id]. A tag the block already
 *  carries is kept while it still closes unambiguously, so text read and
 *  written back unchanged is byte-identical; otherwise one is picked. */
export function contentBlock(id: string, content: string, existingTag = ''): string {
  const keeps = existingTag
    ? !content.includes(`${CONTENT_CLOSE}${existingTag}-->`)
    : !content.includes(`${CONTENT_CLOSE}-->`);
  const tag = keeps ? existingTag : closeTagFor(content);
  return `<!--pondering:content id=${id}${tag}-->\n${content}\n${CONTENT_CLOSE}${tag}-->`;
}

const FRONT_MATTER = /^---\n[\s\S]*?\n---\n*/;

/** Where the machine half of a note begins. Two shapes, because the vault
 *  carries both: the one-line `pondering:unit` trailer the app writes since
 *  its structure rows moved to `.manifests/<id>.jsonl`, and the base64
 *  manifest every note carried before that. A note whose tail this misses is
 *  handed to the editor as prose and saved without it — and a note carrying
 *  neither marker is not one the app recognises as its own, so it re-imports
 *  as a foreign drop and mints a duplicate. */
const MACHINE_TAIL = /<!--pondering:(?:unit |structure base64)/g;

/**
 * Where a note started right now belongs, mirroring `VaultPathScheme` — month
 * bucket, `Note <local timestamp>`, local time because the filename is read by
 * a person even though the shard keys elsewhere are UTC.
 *
 * [taken] guards the one collision the scheme allows: two notes in the same
 * minute are the same filename, and the second would silently replace the
 * first.
 */
export function newNotePath(at: Date, taken: (path: string) => boolean = () => false): string {
  const year = String(at.getFullYear());
  const month = two(at.getMonth() + 1);
  const stamp = `${year}-${month}-${two(at.getDate())} at ${two(at.getHours())}-${two(at.getMinutes())}`;
  const base = `${year}/${month}/Note ${stamp}`;

  let candidate = `${base}.md`;
  // Bounded because `taken` reads a real vault: a run this long means the
  // predicate is answering something other than the question.
  for (let n = 2; n < 100 && taken(candidate); n++) candidate = `${base} (${n}).md`;
  return candidate;
}

/** The prose a plain editor may edit, or null when this file is not one run of
 *  text and editing it as one would destroy its structure. */
export function editableText(raw: string): string | null {
  const { rest } = split(raw);
  const blocks = blocksIn(rest);
  if (blocks.length > 1) return null;
  if (blocks.length === 1) return blocks[0]![3]!;
  return rest.replace(/^\n+/, '').replace(/\s+$/, '');
}

const PLACED_BLOCK = /<!--pondering:block [^>]*-->[\s\S]*?<!--pondering:\/block-->/g;

/**
 * A note's own text as the app holds it (`VaultSqlIo.ownTexts`): the messages of its body a line
 * apart, leaving out those of blocks placed below it, or all of it where nothing has imported it.
 */
export function ownText(raw: string): string {
  const { rest } = split(raw);
  const own = blocksIn(rest.replace(PLACED_BLOCK, ''));
  if (own.length > 0) return own.map((block) => block[3]!).join('\n');
  if (blocksIn(rest).length > 0) return '';
  return rest.replace(/^\n+/, '').replace(/\s+$/, '');
}

/**
 * [next] written back into [raw], preserving everything the editor did not
 * show: front matter, the marker and id around the block, and the manifest.
 *
 * A file [editableText] refuses is returned unchanged — a refusal that wrote
 * anyway would be the one outcome worth avoiding here.
 */
export function withText(raw: string, next: string): string {
  const { head, rest, tail } = split(raw);
  const blocks = blocksIn(rest);
  if (blocks.length > 1) return raw;

  const text = next.replace(/\s+$/, '');

  if (blocks.length === 1) {
    const block = blocks[0]!;
    const rebuilt = contentBlock(block[1]!, text, block[2]);
    return head + rest.slice(0, block.index) + rebuilt + rest.slice(block.index + block[0].length) + tail;
  }

  const body = text ? `${text}\n` : '';
  return tail ? `${head}${body}\n${tail}` : `${head}${body}`;
}

/**
 * What the next save starts from when the file moved while its field was being
 * typed in — the app re-renders every save, giving the prose a marker and an id.
 *
 * The newer file, so the save lands inside that marker and the app reads it as
 * an edit. The editor's own copy is marker-less, and prose written over a
 * render without its marker reads to an older app as prose typed beside the
 * note: each save came back as another copy of the whole text. The own copy
 * only where the newer file is not one run of prose, since [withText] refuses
 * that file and the save would be dropped.
 */
export function baseWhileTyping(own: string, moved: string): string {
  return editableText(moved) === null ? own : moved;
}

/** The file a thought starts as: its text, under front matter that only says
 *  which unit it is, so every device that mints it arrives at the same note. No
 *  origin, which would make the thought a held note; the write adds when it was
 *  made. */
export function newNoteFile(text: string, id: string): string {
  return withText(`---\nid: ${id}\n---\n\n`, text);
}

/** Who wrote a held note, as the app keeps it (`noteOrigin`). Not
 *  `assistant`: that marks the tools' own writing, which `update_note` may
 *  rewrite, and this is the person's. */
export const NOTE_ORIGIN_WEB = 'web';

/** The heading the app renders above a note's prose; a device's re-render of a
 *  note written in this shape changes nothing a reader sees. */
const NOTE_HEADING = '## Note';

/**
 * The file a note a nook holds starts as — Write's default note. Declaring
 * [id] lets every device that adopts it arrive at the same note, where each
 * would otherwise mint its own.
 */
export function newHeldNoteFile(text: string, id: string): string {
  return withText(`---\nid: ${id}\nkind: note\norigin: ${NOTE_ORIGIN_WEB}\n---\n\n${NOTE_HEADING}\n`, text);
}

/**
 * The three regions of a vault file. [rest] is the body — what an editor may
 * work on — and [head] and [tail] are carried through untouched, which is what
 * makes a write from this surface a readable edit rather than a rewrite.
 */
function split(raw: string): { head: string; rest: string; tail: string } {
  const front = raw.match(FRONT_MATTER);
  let head = front ? front[0] : '';
  // A face's picks and a nook's member lines render above the writing and are
  // the app's references, not prose: they ride with the head, the same region
  // the importer reads a unit's typed prose from after.
  for (const list of LEADING_LISTS) {
    const at = raw.slice(head.length).match(list);
    if (at) head += at[0];
  }
  const after = raw.slice(head.length);
  const at = machineTailAt(after);
  return at < 0
    ? { head, rest: after, tail: '' }
    : { head, rest: after.slice(0, at), tail: after.slice(at) };
}

const LEADING_LISTS = [/^# Face\n(?:- .*\n)*\n*/, /^## Members\n(?:- \[.*\n)*\n*/, /^## Note\n/];

/** The FIRST machine marker, so everything from there down is carried through
 *  untouched — a note quoting a marker in its prose keeps its own words in the
 *  body and simply protects a little more than it must. */
function machineTailAt(after: string): number {
  MACHINE_TAIL.lastIndex = 0;
  return MACHINE_TAIL.exec(after)?.index ?? -1;
}

function blocksIn(rest: string): RegExpExecArray[] {
  return [...rest.matchAll(CONTENT_BLOCK)] as RegExpExecArray[];
}

/** The month folder a note started at [at] goes into — what to check for a
 *  taken name before minting one. */
export function monthPrefix(at: Date): string {
  return `${at.getFullYear()}/${two(at.getMonth() + 1)}/`;
}

function two(n: number): string {
  return String(n).padStart(2, '0');
}
